import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import MapEditLocation from "../Maps/MapEditLocation";

import {
    getUserProfile,
    UserProfile,
} from "../../API/UserProfileApi/GetUserProfile";

import { viewAddressByIdAsync } from "../../Admin-Api/ViewAddressByIdAsync";

import { Address } from "../../API/Address/ViewAllAddedAddressByUserAsync";

import { updateLocalStorageAddressData } from "../../utils/updateLocalStorageAddressData";

import { useLocalization } from "../Context/MultiLanguageContext";

import { updateAddressAsync } from "../../Admin-Api/UpdateAddressAsync";

import { confirmUpdateLocationWithPamCodeChangeAsync } from "../../Admin-Api/ConfirmUpdateAddress";

import { addressPrivacy } from "../../API/Address/AddressPrivacy";

import { getNearbyPinCodesPamCodeAsync } from "../../API/Address/GetNearbyPinCodesPamCodeAsync";

interface EditAddressAdminProps {
    updateUserName: (newUserName: string) => void;
}

interface PinCodeData {
    pinCode: string;
    blockCode: string;
    pamCode: string;
}

interface FormData {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    pamCode: string;
    addressType: string;
    pinCode: string;
    blockCode: string;
}

interface UpdateApiResponse {
    message: string;
    isPamCodeChanged: boolean;
    predictedPamCode: string;
}

const EditAddressAdmin: React.FC<EditAddressAdminProps> = ({
    updateUserName,
}) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useLocalization();
    const locationState = location.state as { displayPamCode?: string } | null;

    const [formData, setFormData] = useState<FormData>({
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        pamCode: "",
        addressType: "",
        pinCode: "",
        blockCode: "",
    });

    const [selectedAddressType, setSelectedAddressType] =
        useState("home");

    const [customAddressType, setCustomAddressType] =
        useState("");

    const [errorMessage, setErrorMessage] = useState("");

    const [loading, setLoading] = useState(true);

    const [userName, setUserName] = useState("");

    const [addressDetails, setAddressDetails] =
        useState<Address | null>(null);

    const [address, setAddress] = useState<Address | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [, setUserProfile] = useState<UserProfile | null>(null);

    const [initialCoordinates, setInitialCoordinates] =
        useState<{
            latitude: number;
            longitude: number;
        } | null>(null);

    const [isModalVisible, setIsModalVisible] =
        useState(false);

    const [apiResponse, setApiResponse] =
        useState<UpdateApiResponse | null>(null);

    const [nearbyPinCodes, setNearbyPinCodes] = useState<
        PinCodeData[]
    >([]);

    const [initialPinBlock, setInitialPinBlock] =
        useState("");

    const [displayPamCode, setDisplayPamCode] =
        useState("");

    const formatPamCodeForDisplay = (rawPamCode: string) => {
        const normalized = rawPamCode.replace(/-/g, "");
        if (normalized.length === 10) {
            return `${normalized.slice(0, 6)}-${normalized.slice(6, 10)}`;
        }
        if (normalized.length > 10) {
            return `${normalized.slice(0, 6)}-${normalized.slice(6, 10)}-${normalized.slice(10)}`;
        }
        return rawPamCode;
    };

    const getFullPamCode = () => {
        if (displayPamCode) {
            return formatPamCodeForDisplay(displayPamCode);
        }

        const addressCode = address?.addressCode || "";

        if (
            address?.pinCode &&
            address?.blockCode &&
            addressCode
        ) {
            return `${address.pinCode}-${address.blockCode}-${addressCode}`;
        }

        if (formData.pamCode) {
            return formatPamCodeForDisplay(formData.pamCode);
        }

        return formatPamCodeForDisplay(address?.pamCode || "");
    };

    const fetchNearbyPinCodes = async (
        latitude: number,
        longitude: number,
        initialPamCode?: string,
        updateDisplay: boolean = false
    ) => {
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error(t("sessionExpired"));
            navigate("/");
            return;
        }

        try {
            const formattedPinCodes =
                await getNearbyPinCodesPamCodeAsync(
                    latitude,
                    longitude,
                    token,
                    t
                );

            if (!formattedPinCodes) {
                setNearbyPinCodes([]);
                return;
            }

            setNearbyPinCodes(formattedPinCodes);

            const pinBlockToUse =
                initialPamCode || initialPinBlock;

            if (pinBlockToUse) {
                const existingPin = formattedPinCodes.find(
                    (pin) =>
                        `${pin.pinCode}${pin.blockCode}` ===
                        pinBlockToUse
                );

                if (existingPin) {
                    setFormData((previous) => ({
                        ...previous,
                        pinCode: existingPin.pinCode,
                        blockCode: existingPin.blockCode,
                        pamCode: existingPin.pamCode,
                    }));

                    if (updateDisplay) {
                        setDisplayPamCode(existingPin.pamCode);
                    }
                    return;
                }
            }

            if (formattedPinCodes.length > 0) {
                const recommendedPin = formattedPinCodes[0];

                setFormData((previous) => ({
                    ...previous,
                    pinCode: recommendedPin.pinCode,
                    blockCode: recommendedPin.blockCode,
                    pamCode: recommendedPin.pamCode,
                }));

                if (updateDisplay) {
                    setDisplayPamCode(recommendedPin.pamCode);
                }
            }
        } catch (error) {
            console.error(
                "Failed to fetch nearby PIN codes:",
                error
            );
        }
    };


    useEffect(() => {
        const fetchAddressDetails = async () => {
            if (!id) {
                toast.error("Address ID not found.");
                navigate("/manageaddress");
                return;
            }

            const token = localStorage.getItem("token");

            if (!token) {
                toast.error(t("sessionExpired"));
                navigate("/");
                return;
            }

            try {
                setLoading(true);
                setErrorMessage("");

                const fetchedAddress = await viewAddressByIdAsync(
                    id,
                    token,
                    t
                );

                if (!fetchedAddress) {
                    toast.error("Address details not found.");
                    navigate("/manageaddress");
                    return;
                }

                setAddressDetails(fetchedAddress);
                setAddress(fetchedAddress);

                const pinBlock = `${fetchedAddress.pinCode || ""
                    }${fetchedAddress.blockCode || ""}`;

                setInitialPinBlock(pinBlock);

                const loadedAddressType =
                    fetchedAddress.addressType || "";

                const normalizedAddressType =
                    loadedAddressType.toLowerCase();

                if (
                    loadedAddressType &&
                    !["home", "office"].includes(
                        normalizedAddressType
                    )
                ) {
                    setSelectedAddressType("custom");
                    setCustomAddressType(loadedAddressType);
                } else {
                    setSelectedAddressType(
                        normalizedAddressType || "home"
                    );
                }

                setFormData({
                    addressLine1:
                        fetchedAddress.addressLine1 || "",
                    addressLine2:
                        fetchedAddress.addressLine2 || "",
                    addressLine3:
                        fetchedAddress.addressLine3 || "",
                    pamCode: fetchedAddress.pamCode || "",
                    addressType: loadedAddressType,
                    pinCode: fetchedAddress.pinCode || "",
                    blockCode: fetchedAddress.blockCode || "",
                });

                setDisplayPamCode(
                    locationState?.displayPamCode ||
                        (fetchedAddress.pinCode && fetchedAddress.blockCode && fetchedAddress.addressCode
                            ? `${fetchedAddress.pinCode}-${fetchedAddress.blockCode}-${fetchedAddress.addressCode}`
                            : fetchedAddress.pamCode || "")
                );

                const latitude = Number(
                    fetchedAddress.latitude
                );

                const longitude = Number(
                    fetchedAddress.longitude
                );

                if (
                    !Number.isNaN(latitude) &&
                    !Number.isNaN(longitude)
                ) {
                    setInitialCoordinates({
                        latitude,
                        longitude,
                    });

                    await fetchNearbyPinCodes(
                        latitude,
                        longitude,
                        pinBlock,
                        false
                    );
                }
            } catch (error) {
                console.error(
                    "Failed to fetch address details:",
                    error
                );

                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : "Failed to fetch address details."
                );
            } finally {
                setLoading(false);
            }
        };

        fetchAddressDetails();
    }, [id]);


    const handleLocationChange = (
        addressId: string,
        latitude: number,
        longitude: number
    ) => {
        setAddress((previousAddress) => {
            if (
                !previousAddress ||
                String(previousAddress.id) !==
                String(addressId)
            ) {
                return previousAddress;
            }

            return {
                ...previousAddress,
                latitude,
                longitude,
            };
        });

        const locationChanged =
            !initialCoordinates ||
            initialCoordinates.latitude !== latitude ||
            initialCoordinates.longitude !== longitude;

        if (locationChanged) {
            fetchNearbyPinCodes(latitude, longitude, undefined, false);
        }
    };

    // -------------------------------------------------------
    // FORM INPUTS
    // -------------------------------------------------------

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { name, value } = event.target;

        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleAddressTypeChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const value = event.target.value;

        setSelectedAddressType(value);

        if (value === "custom") {
            setCustomAddressType("");

            setFormData((previous) => ({
                ...previous,
                addressType: "",
            }));

            return;
        }

        setFormData((previous) => ({
            ...previous,
            addressType: value,
        }));
    };

    const handleCustomAddressTypeChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;

        setCustomAddressType(value);

        setFormData((previous) => ({
            ...previous,
            addressType: value,
        }));
    };

    const handlePinCodeChangeSelect = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const selectedPamCode = event.target.value;

        const selectedPin = nearbyPinCodes.find(
            (pin) => pin.pamCode === selectedPamCode
        );

        if (!selectedPin) return;

        setFormData((previous) => ({
            ...previous,
            pinCode: selectedPin.pinCode,
            blockCode: selectedPin.blockCode,
            pamCode: selectedPin.pamCode,
        }));
    };


    const validateForm = () => {
        if (!formData.addressLine1.trim()) {
            toast.error(
                "Please enter the building number."
            );
            return false;
        }

        if (!formData.addressLine2.trim()) {
            toast.error("Please enter the street.");
            return false;
        }

        if (!formData.addressLine3.trim()) {
            toast.error("Please enter the city.");
            return false;
        }

        if (!formData.addressType.trim()) {
            toast.error(
                "Please select or enter an address type."
            );
            return false;
        }

        if (!address?.latitude || !address?.longitude) {
            toast.error(t("locationNotSelected"));
            return false;
        }

        if (!formData.pinCode || !formData.blockCode) {
            toast.error(
                "Please select a valid postal code."
            );
            return false;
        }

        return true;
    };

    // -------------------------------------------------------
    // UPDATE ADDRESS
    // -------------------------------------------------------

    const handleUpdate = async () => {
        if (!validateForm() || !id) return;

        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            setIsSubmitting(true);

            const payload = {
                addressLine1: formData.addressLine1.trim(),
                addressLine2: formData.addressLine2.trim(),
                addressLine3: formData.addressLine3.trim(),
                addressType: formData.addressType.trim(),
                latitude: String(address?.latitude),
                longitude: String(address?.longitude),
                pamCode: formData.pamCode,
            };

            const response = await updateAddressAsync(
                id,
                payload,
                token,
                t
            );

            if (response.isPamCodeChanged) {
                setApiResponse(response);
                setIsModalVisible(true);
                setIsSubmitting(false);
                return;
            }

            toast.success(response.message);
            navigate("/manageaddress");
        } catch (err) {
            toast.error("Failed to update address");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmUpdate = async () => {
        if (!id || !apiResponse) return;

        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            setIsSubmitting(true);

            const [pinCode, blockCode, addressCode] =
                apiResponse.predictedPamCode.split("-");

            const payload = {
                addressLine1: formData.addressLine1.trim(),
                addressLine2: formData.addressLine2.trim(),
                addressLine3: formData.addressLine3.trim(),
                addressType: formData.addressType.trim(),
                latitude: String(address?.latitude),
                longitude: String(address?.longitude),
                pinCode,
                blockCode,
                addressCode,
            };

            const result =
                await confirmUpdateLocationWithPamCodeChangeAsync(
                    id,
                    payload,
                    token,
                    t
                );

            toast.success(result.message);
            setIsModalVisible(false);
            navigate("/manageaddress");
        } catch (err) {
            toast.error("Failed to confirm update");
        } finally {
            setIsSubmitting(false);
        }
    };


    const togglePrivacy = async () => {
        if (!id || !address) return;

        const token = localStorage.getItem("token");

        if (!token) {
            toast.error(t("sessionExpired"));
            navigate("/");
            return;
        }

        const previousPrivacy = Boolean(
            address.isPrivate
        );

        setAddress((previous) =>
            previous
                ? {
                    ...previous,
                    isPrivate: !previousPrivacy,
                }
                : previous
        );

        try {
            await addressPrivacy(Number(id), token, t);
        } catch (error) {
            console.error(
                "Failed to update address privacy:",
                error
            );

            setAddress((previous) =>
                previous
                    ? {
                        ...previous,
                        isPrivate: previousPrivacy,
                    }
                    : previous
            );

            toast.error(
                "Failed to update address privacy."
            );
        }
    };

    // -------------------------------------------------------
    // PROFILE AND LOGOUT
    // -------------------------------------------------------

    useEffect(() => {
        const storedUserName =
            localStorage.getItem("userName");

        if (storedUserName) {
            setUserName(storedUserName);
        }
    }, []);

    const fetchAndHandleUserProfile = async () => {
        try {
            const profile = await getUserProfile(t);

            setUserProfile(profile);
            setUserName(profile.userName);
            updateUserName(profile.userName);

            navigate("/profile", {
                state: {
                    userName: profile.userName,
                    email: profile.email,
                    phoneNumber: profile.phoneNumber,
                },
            });
        } catch {
            toast.error(t("sessionExpired"));
            navigate("/home");
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("userToken");
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        localStorage.removeItem("userName");

        sessionStorage.clear();

        navigate("/", {
            replace: true,
        });
    };

    // -------------------------------------------------------
    // SKELETON
    // -------------------------------------------------------

    const EditAddressSkeleton = () => (
        <div className="w-full p-4 sm:p-8 bg-white rounded-lg shadow-md animate-pulse">
            <div className="h-[250px] w-full bg-gray-200 rounded-lg mb-6" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                    <div key={item}>
                        <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
                        <div className="h-10 w-full bg-gray-200 rounded" />
                    </div>
                ))}
            </div>
        </div>
    );

    // -------------------------------------------------------
    // RENDER
    // -------------------------------------------------------

    return (
        <div className="flex flex-col min-h-screen bg-gray-100">

            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-8 py-4 bg-white shadow-sm">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                    <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
                    Edit Address
                </h1>

                <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg w-fit">

                    <button
                        type="button"
                        onClick={() => navigate("/manageaddress")}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm border border-blue-700 rounded-lg text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
                    >
                        Back
                    </button>

                </div>
            </header>

            <main className="p-4 sm:p-6 flex-grow">

                {loading ? (
                    <EditAddressSkeleton />
                ) : (
                    <div className="w-full p-4 sm:p-8 bg-white rounded-lg shadow-md">

                        <div className="relative h-[220px] sm:h-[280px] md:h-[350px] overflow-hidden rounded-lg border border-gray-200">
                            {address ? (
                                <MapEditLocation
                                    addresses={[address]}
                                    onLocationChange={
                                        handleLocationChange
                                    }
                                />
                            ) : (
                                <div className="flex h-full items-center justify-center">
                                    <p className="text-center text-gray-500">
                                        {t("loadingMap")}
                                    </p>
                                </div>
                            )}
                        </div>

                        <form
                            className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6"
                            onSubmit={(event) => {
                                event.preventDefault();
                                handleUpdate();
                            }}
                        >
                            <div>
                                <label
                                    htmlFor="addressLine1"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {t("buildingNumber")}
                                </label>

                                <input
                                    type="text"
                                    id="addressLine1"
                                    name="addressLine1"
                                    value={formData.addressLine1}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-slate-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="addressLine2"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {t("street")}
                                </label>

                                <input
                                    type="text"
                                    id="addressLine2"
                                    name="addressLine2"
                                    value={formData.addressLine2}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-slate-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="addressLine3"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {t("city")}
                                </label>

                                <input
                                    type="text"
                                    id="addressLine3"
                                    name="addressLine3"
                                    value={formData.addressLine3}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-slate-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="pamCode"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {t("pamCode")}
                                </label>

                                <input
                                    type="text"
                                    id="pamCode"
                                    name="pamCode"
                                    value={getFullPamCode()}
                                    readOnly
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-100 text-gray-600"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="addressType"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {t("addressType")}
                                </label>

                                <select
                                    id="addressType"
                                    name="addressType"
                                    value={selectedAddressType}
                                    onChange={handleAddressTypeChange}
                                    className="w-full rounded-lg border border-gray-200 bg-slate-50 py-2.5 pl-4 pr-10 outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="home">
                                        {t("home")}
                                    </option>

                                    <option value="office">
                                        {t("office")}
                                    </option>

                                    <option value="custom">
                                        {t("other")}
                                    </option>
                                </select>

                                {selectedAddressType === "custom" && (
                                    <input
                                        type="text"
                                        value={customAddressType}
                                        onChange={
                                            handleCustomAddressTypeChange
                                        }
                                        placeholder={t("enterAddressType")}
                                        className="mt-2 w-full rounded-lg border border-gray-200 bg-slate-50 py-2.5 px-4 outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="pinCodeDropdown"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {t("postalCode")}
                                </label>

                                <select
                                    id="pinCodeDropdown"
                                    name="pinCodeDropdown"
                                    value={formData.pamCode}
                                    onChange={handlePinCodeChangeSelect}
                                    disabled={
                                        nearbyPinCodes.length === 0
                                    }
                                    className="w-full rounded-lg border border-gray-200 bg-slate-50 py-2.5 px-4 outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {nearbyPinCodes.length === 0 && (
                                        <option value="">
                                            No postal codes available
                                        </option>
                                    )}

                                    {nearbyPinCodes.map((pin, index) => (
                                        <option
                                            key={`${pin.pinCode}-${pin.blockCode}-${index}`}
                                            value={pin.pamCode}
                                        >
                                            {pin.pinCode}
                                            {index === 0
                                                ? " (Recommended)"
                                                : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-span-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-2">
                                <div className="flex items-center gap-3">
                                    <label
                                        htmlFor="privacySwitch"
                                        className="text-sm font-medium text-gray-700"
                                    >
                                        Address Privacy:
                                    </label>

                                    <span className="text-sm font-medium text-gray-700">
                                        {address?.isPrivate
                                            ? t("private")
                                            : t("public")}
                                    </span>

                                    <input
                                        type="checkbox"
                                        id="privacySwitch"
                                        checked={Boolean(
                                            address?.isPrivate
                                        )}
                                        onChange={togglePrivacy}
                                        disabled={isSubmitting}
                                        className="cursor-pointer relative w-11 h-6 rounded-full appearance-none bg-gray-300 checked:bg-blue-600 transition-colors duration-200 outline-none before:content-[''] before:absolute before:w-5 before:h-5 before:rounded-full before:bg-white before:shadow before:top-0.5 before:left-0.5 checked:before:translate-x-5 before:transition-transform before:duration-200 disabled:cursor-not-allowed disabled:opacity-60"
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate("/manageaddress")
                                        }
                                        disabled={isSubmitting}
                                        className="py-2 px-4 border border-gray-300 bg-white text-gray-700 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {t("cancel")}
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`inline-flex items-center gap-2 py-2 px-4 text-white rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500 ${isSubmitting
                                            ? "bg-gray-400 cursor-not-allowed"
                                            : "bg-blue-600 hover:bg-blue-700"
                                            }`}
                                    >
                                        {isSubmitting && (
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        )}

                                        {isSubmitting
                                            ? t("updating")
                                            : t("update")}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </main>


            {isModalVisible && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
                    <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
                        {/* Header */}
                        <div className="border-b px-6 py-4">
                            <h2 className="text-lg font-semibold text-gray-800">
                                Confirm Update
                            </h2>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5">
                            <p className="text-gray-700">
                                PAM Code may differ based on new location.
                            </p>

                            <p className="mt-3 text-sm">
                                <span className="font-semibold">New PAM Code:</span>{" "}
                                <span className="text-blue-600 font-semibold">
                                    {apiResponse?.predictedPamCode}
                                </span>
                            </p>

                            <p className="mt-3 text-gray-600">
                                Do you want to continue updating this address?
                            </p>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 border-t px-6 py-4">
                            <button
                                type="button"
                                onClick={() => setIsModalVisible(false)}
                                disabled={isSubmitting}
                                className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={handleConfirmUpdate}
                                disabled={isSubmitting}
                                className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isSubmitting ? "Updating..." : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default EditAddressAdmin;